<script setup>
const props = defineProps({
  id: {
    type: String,
    default: 'search-mask',
  },
  show: {
    type: Boolean,
    default: false,
  },
})

const config = useRuntimeConfig()
const { t, locale } = useI18n()
const localeRoute = useLocaleRoute()

const searchStore = useSearchStore()
const seoStore = useSeoStore()
const { q, filter } = storeToRefs(searchStore)
const results = ref(null)

const modal = ref(null)
const searchInput = ref(null)

const pageIsMagazine = computed(() => {
  return seoStore?.pageType?.includes('cms.Magazine')
})

const queryFilter = computed(() => {
  const filterResult = filter.value?.filter(({ name }) => name).map(({ name }) => name) || results.value?._meta?.filter
    ?.filter(({ name }) => name)
    .map(({ name }) => name)
    || []

  if (pageIsMagazine.value && !filterResult.includes('products')) {
    filterResult.push('!products')
  }

  return filterResult.join(',')
})

const queryString = computed(() => {
  return q.value?.trim() || ''
})

const headers = computed(() => {
  return [['Accept-Language', locale.value]]
})

function clear() {
  q.value = ''
  filter.value.splice(0)
  results.value = null
  focus()
}

function resetFilter() {
  filter.value.splice(0)
  results.value = null
}

function close() {
  q.value = ''
  modal.value.hide()
}

function focus() {
  searchInput.value.focus()
}

function toggleFilter(f) {
  focus()
  const m = filter.value.map(f => f.name)
  if (m.includes(f.name)) {
    const index = m.indexOf(f.name)
    if (index > -1) {
      filter.value.splice(index, 1)
    }
  }
  else {
    filter.value.push(f)
  }
}

const { data, execute } = await useFetch(`/search/mask/`, {
  method: 'GET',
  server: false,
  baseURL: config.public.apiBase,
  headers,
  query: { q: queryString, filter: queryFilter },
  watch: false,
  immediate: false,
})

function handleShowing() {
  resetFilter()
  if (queryString.value) {
    execute()
  }
}

function onModalShown() {
  focus()
  handleShowing()
}

async function redirect() {
  modal.value.hide()
  navigateTo(
    localeRoute({
      name: 'search',
      query: { q: q.value, filter: queryFilter.value || undefined },
    }),
  )
}

watch(data, (newData) => {
  results.value = newData
})

watch([queryString, queryFilter], () => {
  if (!queryString.value) {
    filter.value.splice(0)
    results.value = null
    return
  }
  execute()
})
</script>

<template>
  <div
    class="search"
    :class="{
      'search--no-results': results?._meta?.total === 0,
      'search--results': results?._meta?.total > 0,
    }"
  >
    <Modal
      :id="props.id"
      ref="modal"
      :required="false"
      :close="false"
      :fade="false"
      :show="props.show"
      @show="onModalShown"
    >
      <template #header>
        <div class="d-flex flex-column w-100 h-100">
          <div class="d-flex align-items-center search__input">
            <IconsSearch class="search__icon" />
            <form
              role="search"
              action="."
              class="search__form"
              @submit.prevent
            >
              <input
                id="search-input"
                ref="searchInput"
                v-model="q"
                type="search"
                class="equal-base equal-base--tighter"
                :placeholder="t('search.mask.placeholder')"
                autocomplete="off"
                data-cy="search-mask-input"
                @keyup.enter="redirect"
              >
            </form>
            <div
              v-show="q"
              class="search__clear flex-shrink-0"
              @click="clear"
            >
              <IconsClose />
            </div>
          </div>
          <ul
            v-if="
              results?._meta?.total > 0
                || (filter?.filter(String).length > 0
                  && results?._meta?.filter?.length > 0)
            "
            class="search__tags"
          >
            <li
              v-for="item in results?._meta?.filter"
              :key="item?.name"
            >
              <Tag
                :name="item?.verbose_name"
                class="tag"
                :class="{
                  'tag--checked': filter
                    .map((f) => f.name)
                    .includes(item?.name),
                }"
                @click="toggleFilter(item)"
              />
            </li>
          </ul>
        </div>
      </template>
      <template
        v-if="results?._meta?.total > 0"
        #default
      >
        <template
          v-for="section in results?.data"
          :key="section?.name"
        >
          <div
            v-if="section?._meta?.total > 0"
            class="result"
          >
            <div class="equal-small-4 result__category">
              {{ section?._meta?.verbose_name_plural }}
            </div>
            <ul class="result__list">
              <li
                v-for="hit in section?.results"
                :key="hit?.name"
                class="equal-small-2"
              >
                <NuxtLink
                  :to="hit?.to"
                  @click="close"
                >
                  <div
                    class="result__name"
                    v-html="hit?.name"
                  />
                </NuxtLink>
              </li>
            </ul>
          </div>
        </template>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/variables.scss';
@import 'bootstrap/scss/mixins';

.search {
  transition: 0.5s ease;

  :deep(.modal) {
    z-index: 1100;
  }

  &--no-results {
    :deep(.modal-content-box) {
      border-radius: 25px !important;
      transition: 0.5s ease;
    }
  }

  &--results {
    :deep(.modal-content-box) {
      border-radius: 25px !important;
      transition: 0.5s ease;
    }

    :deep(.modal-header) {
      height: 100% !important;
    }
  }

  :deep(.layout__row) {
    height: auto !important;
  }

  :deep(.layout__col) {
    width: 800px;
  }

  :deep(.modal-dialog) {
    align-items: start;
    height: 200px;

    @include media-breakpoint-down(880) {
      padding: 0 !important;
    }
  }

  :deep(.modal-content) {
    max-height: unset;
  }
  @include media-breakpoint-down(880) {
    :deep(.modal-content) {
      max-height: calc(100dvh - 120px);
    }
  }

  :deep(.modal-content-box) {
    max-height: 415px !important;
    margin-top: 220px;
    margin-bottom: 220px;
    border-radius: 25px !important;
    min-height: 50px;

    @include media-breakpoint-down(880) {
      max-height: unset !important;
      margin-top: 40px;
      margin-bottom: 20px;
    }
  }

  :deep(.modal-header) {
    padding: 0 !important;
    // height: 30px;
  }

  :deep(.modal-body) {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  &__icon {
    fill: $color-disabled;
    width: 18px;
    height: 18px;
    margin-left: 20px;
    margin-right: 10px;
  }

  &__input {
    width: 100%;
    position: relative;

    input {
      width: 100%;
      color: $color-light;
      outline: unset;
      background-color: unset;
      border: 0;
      text-transform: none;
      height: 30px;
    }
  }

  &__clear {
    background-color: $color-plain-grey-10;
    height: 30px;
    width: 30px;
    display: flex;
    align-items: center;
    margin-left: 20px;
    border-radius: 100%;
    border: 1px solid $color-plain-grey-30;
    position: absolute;
    right: 0;
    top: 0;

    cursor: pointer;
    &:hover,
    &:focus,
    &:active {
      background-color: $color-plain-grey-20;
      border: none;
    }
    svg {
      margin: auto;
      fill: #999999;
      width: 12px;
      height: 12px;
    }
  }

  &__tags {
    margin: 10px -10px;
    padding: 15px 30px 0 30px;
    list-style: none;
    display: flex;
    flex-wrap: wrap;

    border-top: 1px solid $color-dark-01;
    border-bottom: 1px solid $color-dark-01;

    li {
      &:not(:last-child) {
        margin-right: 15px;
      }

      margin-bottom: 15px;
    }
  }
}

.tag {
  background-color: $color-plain-grey-15;
  &--checked {
    border: 1px solid #808080;
    background-color: $color-dark-01;
    &:hover,
    &:focus,
    &:active {
      background-color: $color-plain-grey-15 !important;
    }
  }
  &:hover,
  &:focus,
  &:active {
    background-color: $color-plain-grey-20;
  }
}

.result {
  padding: 15px 0;

  &:first-child {
    padding-top: 5px;
  }

  &:not(:last-child) {
    border-bottom: 1px solid $color-dark-01;
  }

  &__category {
    padding-top: 15px;
    padding-bottom: 10px;
    color: $color-disabled;
    font-weight: bold;
    text-transform: uppercase;
  }

  &__list {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      padding: 15px 0;

      a {
        outline: none;
        text-decoration: none;
      }
    }
  }

  &__name {
    font-weight: bold;

    :deep(mark) {
      font-weight: normal;
      background-color: unset;
      color: #999999;
    }
  }
}

:deep(.highlight) {
  font-weight: normal;
  color: $color-disabled;
}
.search__form {
  width: 100%;
}
</style>
